/* eslint-disable no-param-reassign */
import produce from 'immer';
import {
  GET_SMART_FIELD_DEFINITIONS,
  GET_SMART_FIELD_DEFINITIONS_SUCCESS,
  GET_SMART_FIELD_DEFINITIONS_ERROR,
  CLEAR_SMART_FIELD_DEFINITIONS,
  GET_SMART_FIELD_DEFINITION,
  GET_SMART_FIELD_DEFINITION_SUCCESS,
  GET_SMART_FIELD_DEFINITION_ERROR,
  CLEAR_SMART_FIELD_DEFINITION,
  CREATE_SMART_FIELD_DEFINITION,
  CREATE_SMART_FIELD_DEFINITION_SUCCESS,
  CREATE_SMART_FIELD_DEFINITION_ERROR,
  UPDATE_SMART_FIELD_DEFINITION,
  UPDATE_SMART_FIELD_DEFINITION_SUCCESS,
  UPDATE_SMART_FIELD_DEFINITION_ERROR,
  DELETE_SMART_FIELD_DEFINITION,
  DELETE_SMART_FIELD_DEFINITION_SUCCESS,
  DELETE_SMART_FIELD_DEFINITION_ERROR,
  CLEAR_DEFINITION_WAS_DELETED,
  SET_SMART_FIELDS_QUERY,
  CLEAR_SMART_FIELDS_QUERY_FILTER,
  SET_BACK_TO_SMART_FIELDS_LIST,
  SET_CURRENT_NAVIGATION_TAB,
  GET_SMART_FIELD_DEFINITIONS_FOR_PICKER,
  GET_SMART_FIELD_DEFINITIONS_FOR_PICKER_SUCCESS,
  GET_SMART_FIELD_DEFINITIONS_FOR_PICKER_ERROR,
  GET_GROUP_MEMBER_SMART_FIELDS,
  GET_GROUP_MEMBER_SMART_FIELDS_SUCCESS,
  GET_GROUP_MEMBER_SMART_FIELDS_ERROR,
  UPDATE_GROUP_MEMBER_SMART_FIELDS,
  UPDATE_GROUP_MEMBER_SMART_FIELDS_SUCCESS,
  UPDATE_GROUP_MEMBER_SMART_FIELDS_ERROR,
  CLEAR_GROUP_MEMBER_SMART_FIELDS,
  GET_PARTY_GUEST_SMART_FIELDS,
  GET_PARTY_GUEST_SMART_FIELDS_SUCCESS,
  GET_PARTY_GUEST_SMART_FIELDS_ERROR,
  UPDATE_PARTY_GUEST_SMART_FIELDS,
  UPDATE_PARTY_GUEST_SMART_FIELDS_SUCCESS,
  UPDATE_PARTY_GUEST_SMART_FIELDS_ERROR,
  CLEAR_PARTY_GUEST_SMART_FIELDS,
  GET_CONTACT_SMART_FIELDS,
  GET_CONTACT_SMART_FIELDS_SUCCESS,
  GET_CONTACT_SMART_FIELDS_ERROR,
  UPDATE_CONTACT_SMART_FIELDS,
  UPDATE_CONTACT_SMART_FIELDS_SUCCESS,
  UPDATE_CONTACT_SMART_FIELDS_ERROR,
  CLEAR_CONTACT_SMART_FIELDS,
} from 'src/actions/smartFieldsActions';

const backToSmartFieldsList = {
  definitions: [],
  query: {},
  active: false,
  comeBack: false,
  showSystemFields: false,
  expandedCategories: {},
  backToId: null,
};

const defaultQuery = {
  entityType: 'All',
  status: 'Active',
  sort: [['sort', 'asc']], // Default sort by Sort Order ascending
  filters: {},
};

const initialState = {
  definitions: [],
  loading: false,
  query: { ...defaultQuery },
  currentNavigationTab: 'definitions',
  definitionDetails: {
    definition: {},
    definitionWasDeleted: false,
    loading: false,
  },
  backToSmartFieldsList,
  // Picker-specific state (for dynamic field picker in Group-context screens)
  pickerDefinitions: [],
  pickerLoading: false,
  // Group Member Smart Fields state
  groupMemberSmartFields: {
    groupMemberId: null,
    contactId: null,
    groupId: null,
    smartFields: {},
    loading: false,
    saving: false,
  },
  // Party Guest Smart Fields state
  partyGuestSmartFields: {
    partyGuestId: null,
    contactId: null,
    partyId: null,
    smartFields: {},
    loading: false,
    saving: false,
  },
  // Contact Smart Fields state
  contactSmartFields: {
    contactId: null,
    smartFields: {},
    loading: false,
    saving: false,
  },
};

const smartFieldsReducer = (state = initialState, action) => {
  const { type, payload } = action;

  switch (type) {
    case GET_SMART_FIELD_DEFINITIONS: {
      return produce(state, (draft) => {
        draft.loading = true;
      });
    }

    case GET_SMART_FIELD_DEFINITIONS_SUCCESS: {
      return produce(state, (draft) => {
        draft.definitions = payload || [];
        draft.loading = false;
      });
    }

    case GET_SMART_FIELD_DEFINITIONS_ERROR: {
      return produce(state, (draft) => {
        draft.loading = false;
      });
    }

    case CLEAR_SMART_FIELD_DEFINITIONS: {
      return produce(state, (draft) => {
        draft.definitions = [];
      });
    }

    case GET_SMART_FIELD_DEFINITION:
    case CREATE_SMART_FIELD_DEFINITION:
    case UPDATE_SMART_FIELD_DEFINITION: {
      return produce(state, (draft) => {
        draft.definitionDetails.loading = true;
      });
    }

    case GET_SMART_FIELD_DEFINITION_SUCCESS: {
      return produce(state, (draft) => {
        draft.definitionDetails.definition = payload || {};
        draft.definitionDetails.loading = false;
      });
    }

    case GET_SMART_FIELD_DEFINITION_ERROR:
    case CREATE_SMART_FIELD_DEFINITION_SUCCESS:
    case CREATE_SMART_FIELD_DEFINITION_ERROR:
    case UPDATE_SMART_FIELD_DEFINITION_ERROR: {
      return produce(state, (draft) => {
        draft.definitionDetails.loading = false;
      });
    }

    case UPDATE_SMART_FIELD_DEFINITION_SUCCESS: {
      return produce(state, (draft) => {
        draft.definitionDetails.definition = payload || state.definitionDetails.definition;
        draft.definitionDetails.loading = false;
      });
    }

    case CLEAR_SMART_FIELD_DEFINITION: {
      return produce(state, (draft) => {
        draft.definitionDetails.definition = {};
      });
    }

    case DELETE_SMART_FIELD_DEFINITION: {
      return produce(state, (draft) => {
        draft.loading = true;
        draft.definitionDetails.loading = true;
      });
    }

    case DELETE_SMART_FIELD_DEFINITION_SUCCESS: {
      return produce(state, (draft) => {
        draft.definitionDetails.loading = false;
        draft.loading = false;
        draft.definitionDetails.definitionWasDeleted = true;
      });
    }

    case DELETE_SMART_FIELD_DEFINITION_ERROR: {
      return produce(state, (draft) => {
        draft.definitionDetails.loading = false;
        draft.loading = false;
      });
    }

    case CLEAR_DEFINITION_WAS_DELETED: {
      return produce(state, (draft) => {
        draft.definitionDetails.definitionWasDeleted = false;
      });
    }

    case SET_SMART_FIELDS_QUERY: {
      return produce(state, (draft) => {
        draft.query = payload ? {
          ...state.query,
          ...payload
        } : { ...defaultQuery };
      });
    }

    case CLEAR_SMART_FIELDS_QUERY_FILTER: {
      return produce(state, (draft) => {
        draft.query.filters = {};
      });
    }

    case SET_BACK_TO_SMART_FIELDS_LIST: {
      return produce(state, (draft) => {
        draft.backToSmartFieldsList = payload ? {
          ...state.backToSmartFieldsList,
          ...payload,
        } : backToSmartFieldsList;
      });
    }

    case SET_CURRENT_NAVIGATION_TAB: {
      return produce(state, (draft) => {
        draft.currentNavigationTab = payload !== false ? payload : 'definitions';
      });
    }

    // Picker-specific actions
    case GET_SMART_FIELD_DEFINITIONS_FOR_PICKER: {
      return produce(state, (draft) => {
        draft.pickerLoading = true;
      });
    }

    case GET_SMART_FIELD_DEFINITIONS_FOR_PICKER_SUCCESS: {
      return produce(state, (draft) => {
        draft.pickerDefinitions = payload || [];
        draft.pickerLoading = false;
      });
    }

    case GET_SMART_FIELD_DEFINITIONS_FOR_PICKER_ERROR: {
      return produce(state, (draft) => {
        draft.pickerLoading = false;
      });
    }

    // Group Member Smart Fields actions
    case GET_GROUP_MEMBER_SMART_FIELDS: {
      return produce(state, (draft) => {
        draft.groupMemberSmartFields.loading = true;
      });
    }

    case GET_GROUP_MEMBER_SMART_FIELDS_SUCCESS: {
      return produce(state, (draft) => {
        draft.groupMemberSmartFields.groupMemberId = payload?.groupMemberId || null;
        draft.groupMemberSmartFields.contactId = payload?.contactId || null;
        draft.groupMemberSmartFields.groupId = payload?.groupId || null;
        draft.groupMemberSmartFields.smartFields = payload?.smartFields || {};
        draft.groupMemberSmartFields.loading = false;
      });
    }

    case GET_GROUP_MEMBER_SMART_FIELDS_ERROR: {
      return produce(state, (draft) => {
        draft.groupMemberSmartFields.loading = false;
      });
    }

    case UPDATE_GROUP_MEMBER_SMART_FIELDS: {
      return produce(state, (draft) => {
        draft.groupMemberSmartFields.saving = true;
      });
    }

    case UPDATE_GROUP_MEMBER_SMART_FIELDS_SUCCESS: {
      return produce(state, (draft) => {
        draft.groupMemberSmartFields.smartFields = payload?.smartFields || {};
        draft.groupMemberSmartFields.saving = false;
      });
    }

    case UPDATE_GROUP_MEMBER_SMART_FIELDS_ERROR: {
      return produce(state, (draft) => {
        draft.groupMemberSmartFields.saving = false;
      });
    }

    case CLEAR_GROUP_MEMBER_SMART_FIELDS: {
      return produce(state, (draft) => {
        draft.groupMemberSmartFields = {
          groupMemberId: null,
          contactId: null,
          groupId: null,
          smartFields: {},
          loading: false,
          saving: false,
        };
      });
    }

    // Party Guest Smart Fields actions
    case GET_PARTY_GUEST_SMART_FIELDS: {
      return produce(state, (draft) => {
        draft.partyGuestSmartFields.loading = true;
      });
    }

    case GET_PARTY_GUEST_SMART_FIELDS_SUCCESS: {
      return produce(state, (draft) => {
        draft.partyGuestSmartFields.partyGuestId = payload?.partyGuestId || null;
        draft.partyGuestSmartFields.contactId = payload?.contactId || null;
        draft.partyGuestSmartFields.partyId = payload?.partyId || null;
        draft.partyGuestSmartFields.smartFields = payload?.smartFields || {};
        draft.partyGuestSmartFields.loading = false;
      });
    }

    case GET_PARTY_GUEST_SMART_FIELDS_ERROR: {
      return produce(state, (draft) => {
        draft.partyGuestSmartFields.loading = false;
      });
    }

    case UPDATE_PARTY_GUEST_SMART_FIELDS: {
      return produce(state, (draft) => {
        draft.partyGuestSmartFields.saving = true;
      });
    }

    case UPDATE_PARTY_GUEST_SMART_FIELDS_SUCCESS: {
      return produce(state, (draft) => {
        draft.partyGuestSmartFields.smartFields = payload?.smartFields || {};
        draft.partyGuestSmartFields.saving = false;
      });
    }

    case UPDATE_PARTY_GUEST_SMART_FIELDS_ERROR: {
      return produce(state, (draft) => {
        draft.partyGuestSmartFields.saving = false;
      });
    }

    case CLEAR_PARTY_GUEST_SMART_FIELDS: {
      return produce(state, (draft) => {
        draft.partyGuestSmartFields = {
          partyGuestId: null,
          contactId: null,
          partyId: null,
          smartFields: {},
          loading: false,
          saving: false,
        };
      });
    }

    // Contact Smart Fields actions
    case GET_CONTACT_SMART_FIELDS: {
      return produce(state, (draft) => {
        draft.contactSmartFields.loading = true;
      });
    }

    case GET_CONTACT_SMART_FIELDS_SUCCESS: {
      return produce(state, (draft) => {
        draft.contactSmartFields.contactId = payload?.contactId || null;
        draft.contactSmartFields.smartFields = payload?.smartFields || {};
        draft.contactSmartFields.loading = false;
      });
    }

    case GET_CONTACT_SMART_FIELDS_ERROR: {
      return produce(state, (draft) => {
        draft.contactSmartFields.loading = false;
      });
    }

    case UPDATE_CONTACT_SMART_FIELDS: {
      return produce(state, (draft) => {
        draft.contactSmartFields.saving = true;
      });
    }

    case UPDATE_CONTACT_SMART_FIELDS_SUCCESS: {
      return produce(state, (draft) => {
        draft.contactSmartFields.smartFields = payload?.smartFields || {};
        draft.contactSmartFields.saving = false;
      });
    }

    case UPDATE_CONTACT_SMART_FIELDS_ERROR: {
      return produce(state, (draft) => {
        draft.contactSmartFields.saving = false;
      });
    }

    case CLEAR_CONTACT_SMART_FIELDS: {
      return produce(state, (draft) => {
        draft.contactSmartFields = {
          contactId: null,
          smartFields: {},
          loading: false,
          saving: false,
        };
      });
    }

    default: {
      return state;
    }
  }
};

export default smartFieldsReducer;
